import { FC } from "react"
import { GripVertical, Plus, Trash2 } from "lucide-react"
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "react-beautiful-dnd"
import { ConfigAdminPanel } from "../../../../../config-admin-panel"
import {
  RateTableConfigDetails,
  MortgageTerm,
  LoanType
} from "../../../../../../../../types"

interface Props {
  values: RateTableConfigDetails
  setDetailsField: (field: string, value: any) => void
}

const MORTGAGE_TYPE_OPTIONS: MortgageTerm["type"][] = ["fixed", "arm"]

const reorderItems = <T,>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number
) => {
  if (
    sourceIndex < 0 ||
    destinationIndex < 0 ||
    sourceIndex >= items.length ||
    destinationIndex >= items.length
  ) {
    return items
  }

  const next = [...items]
  const [moved] = next.splice(sourceIndex, 1)
  next.splice(destinationIndex, 0, moved)
  return next
}

interface MortgageTermRowProps {
  term: MortgageTerm
  index: number
  termCount: number
  onMove: (index: number, direction: -1 | 1) => void
  onRemove: (index: number) => void
  onUpdate: (index: number, field: keyof MortgageTerm, value: string) => void
}

const MortgageTermRow: FC<MortgageTermRowProps> = ({
  term,
  index,
  termCount,
  onMove,
  onRemove,
  onUpdate
}) => (
  <Draggable draggableId={`rate-table-mortgage-term-${index}`} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={`grid grid-cols-[32px_90px_80px_1fr_32px] gap-2 items-center px-3 py-2 hover:bg-gray-50 transition-colors ${
          snapshot.isDragging ? "rounded-md bg-white shadow-md" : "bg-white"
        }`}
      >
        <div className="flex flex-col items-center gap-0.5">
          <button
            type="button"
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
            className="text-gray-300 hover:text-gray-500 disabled:opacity-30 transition-colors leading-none"
            title="Move up"
          >
            ▲
          </button>
          <span
            {...provided.dragHandleProps}
            className="cursor-grab text-gray-300 active:cursor-grabbing"
            aria-label={`Drag mortgage term ${index + 1}`}
          >
            <GripVertical size={12} />
          </span>
          <button
            type="button"
            onClick={() => onMove(index, 1)}
            disabled={index === termCount - 1}
            className="text-gray-300 hover:text-gray-500 disabled:opacity-30 transition-colors leading-none"
            title="Move down"
          >
            ▼
          </button>
        </div>

        <select
          value={term.type}
          onChange={e => onUpdate(index, "type", e.target.value)}
          className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          {MORTGAGE_TYPE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={term.term}
          onChange={e => onUpdate(index, "term", e.target.value)}
          placeholder="30"
          className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />

        <input
          type="text"
          value={term.label}
          onChange={e => onUpdate(index, "label", e.target.value)}
          placeholder="30yr Fixed"
          className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />

        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
        >
          <Trash2 size={13} />
        </button>
      </div>
    )}
  </Draggable>
)

const RateTableTerms: FC<Props> = ({ values, setDetailsField }) => {
  const d = values.details
  const mortgageTerms: MortgageTerm[] = d.mortgage_terms ?? []
  const loanTypes: LoanType[] = d.loan_type ?? []

  // --- Mortgage terms helpers ---
  const updateMortgageTerm = (
    index: number,
    field: keyof MortgageTerm,
    value: string
  ) => {
    const next = mortgageTerms.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    )
    setDetailsField("mortgage_terms", next)
  }

  const removeMortgageTerm = (index: number) => {
    setDetailsField(
      "mortgage_terms",
      mortgageTerms.filter((_, i) => i !== index)
    )
  }

  const addMortgageTerm = () => {
    setDetailsField("mortgage_terms", [
      ...mortgageTerms,
      { type: "fixed", term: "", label: "" }
    ])
  }

  const moveMortgageTerm = (index: number, direction: -1 | 1) => {
    const next = reorderItems(mortgageTerms, index, index + direction)
    if (next === mortgageTerms) return
    setDetailsField("mortgage_terms", next)
  }

  const handleMortgageTermDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const next = reorderItems(
      mortgageTerms,
      result.source.index,
      result.destination.index
    )
    if (next === mortgageTerms) return
    setDetailsField("mortgage_terms", next)
  }

  // --- Loan type helpers ---
  const updateLoanType = (
    index: number,
    field: keyof LoanType,
    value: string
  ) => {
    const next = loanTypes.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    )
    setDetailsField("loan_type", next)
  }

  const removeLoanType = (index: number) => {
    setDetailsField(
      "loan_type",
      loanTypes.filter((_, i) => i !== index)
    )
  }

  const addLoanType = () => {
    setDetailsField("loan_type", [...loanTypes, { value: "", label: "" }])
  }

  return (
    <div className="space-y-8 pt-2">
      {/* Mortgage Terms */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">
              Mortgage terms
            </h3>
            <p className="text-xs text-gray-400">
              Available loan term options shown in the embed
            </p>
          </div>
          <button
            type="button"
            onClick={addMortgageTerm}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Plus size={12} />
            Add term
          </button>
        </div>

        <ConfigAdminPanel className="divide-y divide-gray-100">
          {/* Header row */}
          <div className="grid grid-cols-[32px_90px_80px_1fr_32px] gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <span />
            <span>Type</span>
            <span>Term</span>
            <span>Label</span>
            <span />
          </div>

          {mortgageTerms.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-400 text-center">
              No mortgage terms — add one above
            </div>
          )}

          <DragDropContext onDragEnd={handleMortgageTermDragEnd}>
            <Droppable droppableId="rate-table-mortgage-terms">
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {mortgageTerms.map((term, i) => (
                    <MortgageTermRow
                      key={`${term.type}-${term.term}-${term.label}-${i}`}
                      term={term}
                      index={i}
                      termCount={mortgageTerms.length}
                      onMove={moveMortgageTerm}
                      onRemove={removeMortgageTerm}
                      onUpdate={updateMortgageTerm}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </ConfigAdminPanel>
      </section>

      {/* Loan Types */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Loan types</h3>
            <p className="text-xs text-gray-400">
              Loan type filter options shown in the embed
            </p>
          </div>
          <button
            type="button"
            onClick={addLoanType}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Plus size={12} />
            Add type
          </button>
        </div>

        <ConfigAdminPanel className="divide-y divide-gray-100">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_1fr_32px] gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <span>Value</span>
            <span>Label</span>
            <span />
          </div>

          {loanTypes.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-400 text-center">
              No loan types — add one above
            </div>
          )}

          {loanTypes.map((lt, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center px-3 py-2 hover:bg-gray-50 transition-colors"
            >
              <input
                type="text"
                value={lt.value}
                onChange={e => updateLoanType(i, "value", e.target.value)}
                placeholder="conventional"
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <input
                type="text"
                value={lt.label}
                onChange={e => updateLoanType(i, "label", e.target.value)}
                placeholder="Conventional"
                className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => removeLoanType(i)}
                className="flex items-center justify-center text-gray-300 hover:text-red-400 transition-colors"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </ConfigAdminPanel>
      </section>
    </div>
  )
}

export default RateTableTerms
